import React, { memo, useMemo } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  LinearProgress,
  Avatar,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  ListItemSecondaryAction,
} from '@mui/material';
import {
  Receipt as ReceiptIcon,
  AccountBalance as BankIcon,
  CheckCircle as CheckCircleIcon,
  AttachMoney as MoneyIcon,
  Warning as WarningIcon,
  Schedule as ScheduleIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { cs } from 'date-fns/locale';
import { Invoice } from '../../types/invoice';
import { ABOFile } from '../../store/slices/aboSlice';
import czechTranslations from '../../translations/czech';

// Status configuration with memoization
const getStatusConfig = (status: string) => {
  const configs = {
    new: { 
      color: 'default' as const, 
      icon: <WarningIcon />, 
      label: czechTranslations.invoices.status.new
    },
    pending: { 
      color: 'warning' as const, 
      icon: <ScheduleIcon />, 
      label: czechTranslations.invoices.status.pending
    },
    reviewed: { 
      color: 'info' as const, 
      icon: <ViewIcon />, 
      label: czechTranslations.invoices.status.reviewed
    },
    ready_for_payment: { 
      color: 'primary' as const, 
      icon: <MoneyIcon />, 
      label: czechTranslations.invoices.status.ready_for_payment
    },
    sent_to_bank: { 
      color: 'secondary' as const, 
      icon: <BankIcon />, 
      label: czechTranslations.invoices.status.sent_to_bank
    },
    paid: { 
      color: 'success' as const, 
      icon: <CheckCircleIcon />, 
      label: czechTranslations.invoices.status.paid
    },
    rejected: { 
      color: 'error' as const, 
      icon: <WarningIcon />, 
      label: czechTranslations.invoices.status.rejected
    },
    default: { 
      color: 'default' as const, 
      icon: <WarningIcon />, 
      label: status
    }
  };
  return configs[status as keyof typeof configs] || configs.default;
};

// ABO Status Labels
const aboStatusLabels = {
  generated: 'Vytvořeno',
  sent: 'Odesláno',
  sent_to_fio: 'Odesláno do FIO',
  error: 'Chyba'
} as const;

// Optimized Stats Card Component
export const OptimizedStatsCard = memo(({ 
  title, 
  value, 
  subtitle,
  icon,
  backgroundColor = '#667eea'
}: {
  title: string;
  value: string | number;
  subtitle?: string;
  icon: React.ReactNode;
  backgroundColor?: string;
}) => {
  const cardStyles = useMemo(() => ({
    background: backgroundColor,
    color: 'white',
    position: 'relative' as const,
    overflow: 'hidden' as const,
    transition: 'transform 0.2s ease-in-out',
    '&:hover': {
      transform: 'translateY(-2px)',
    },
  }), [backgroundColor]);

  return (
    <Card sx={cardStyles}>
      <Box sx={{ position: 'absolute', top: -20, right: -20, opacity: 0.1 }}>
        {icon}
      </Box>
      <CardContent>
        <Typography color="inherit" gutterBottom variant="h6">
          {title}
        </Typography>
        <Typography variant="h3" sx={{ fontWeight: 700 }}>
          {value}
        </Typography>
        {subtitle && (
          <Typography variant="body2" sx={{ opacity: 0.8 }}>
            {subtitle}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
});

OptimizedStatsCard.displayName = 'OptimizedStatsCard';

// Optimized Progress Card Component
export const OptimizedProgressCard = memo(({ 
  title,
  stats 
}: {
  title: string;
  stats: {
    totalInvoices: number;
    newInvoices: number;
    pendingInvoices: number;
    paidInvoices: number;
  };
}) => {
  const progressData = useMemo(() => [
    {
      label: 'Nové faktury',
      value: stats.newInvoices,
      percentage: stats.totalInvoices > 0 ? (stats.newInvoices / stats.totalInvoices) * 100 : 0,
      color: 'primary' as const
    },
    {
      label: 'Čekající na zpracování',
      value: stats.pendingInvoices,
      percentage: stats.totalInvoices > 0 ? (stats.pendingInvoices / stats.totalInvoices) * 100 : 0,
      color: 'warning' as const
    },
    {
      label: 'Zaplacené',
      value: stats.paidInvoices,
      percentage: stats.totalInvoices > 0 ? (stats.paidInvoices / stats.totalInvoices) * 100 : 0,
      color: 'success' as const
    }
  ], [stats]);

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          {title}
        </Typography>
        <Box sx={{ mt: 2 }}>
          {progressData.map((item, index) => (
            <Box key={index} sx={{ mb: index < progressData.length - 1 ? 2 : 0 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2">{item.label}</Typography>
                <Typography variant="body2" color={`${item.color}.main`}>
                  {item.value}
                </Typography>
              </Box>
              <LinearProgress 
                variant="determinate" 
                value={item.percentage}
                color={item.color}
                sx={{ 
                  height: 8, 
                  borderRadius: 4,
                  '& .MuiLinearProgress-bar': {
                    borderRadius: 4,
                  }
                }} 
              />
            </Box>
          ))}
        </Box>
      </CardContent>
    </Card>
  );
});

OptimizedProgressCard.displayName = 'OptimizedProgressCard';

// Optimized Recent Invoices Component
export const OptimizedRecentInvoices = memo(({ 
  invoices,
  onViewAll
}: {
  invoices: Invoice[];
  onViewAll: () => void;
}) => {
  const invoiceItems = useMemo(() => 
    invoices.map((invoice) => {
      const statusConfig = getStatusConfig(invoice.status);
      
      // Format date outside of useMemo hook
      let formattedDate = 'N/A';
      if (invoice.invoiceDate) {
        const date = new Date(invoice.invoiceDate);
        formattedDate = isNaN(date.getTime()) ? 'N/A' : format(date, 'dd.MM.yyyy', { locale: cs });
      }

      return (
        <ListItem key={invoice.id} sx={{ px: 0 }}>
          <ListItemAvatar>
            <Avatar sx={{ bgcolor: 'primary.main' }}>
              <ReceiptIcon />
            </Avatar>
          </ListItemAvatar>
          <ListItemText
            primary={invoice.invoiceNumber}
            secondary={`${invoice.supplierName} • ${formattedDate}`}
          />
          <ListItemSecondaryAction>
            <Chip
              label={statusConfig.label}
              color={statusConfig.color}
              size="small"
              icon={statusConfig.icon}
            />
          </ListItemSecondaryAction>
        </ListItem>
      );
    }), [invoices]
  );

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">
            Poslední faktury
          </Typography>
        </Box>
        <List>
          {invoiceItems}
        </List>
      </CardContent>
    </Card>
  );
});

OptimizedRecentInvoices.displayName = 'OptimizedRecentInvoices';

// Optimized Recent ABO Files Component
export const OptimizedRecentABOFiles = memo(({ 
  aboFiles,
  onViewAll
}: {
  aboFiles: ABOFile[];
  onViewAll: () => void;
}) => {
  const aboItems = useMemo(() => 
    aboFiles.map((aboFile) => (
      <ListItem key={aboFile.id} sx={{ px: 0 }}>
        <ListItemAvatar>
          <Avatar sx={{ bgcolor: 'secondary.main' }}>
            <BankIcon />
          </Avatar>
        </ListItemAvatar>
        <ListItemText
          primary={aboFile.filename}
          secondary={`${aboFile.bankType} • ${aboFile.invoiceCount} záznamů`}
        />
        <ListItemSecondaryAction>
          <Chip
            label={aboStatusLabels[aboFile.status as keyof typeof aboStatusLabels] || aboFile.status}
            color={aboFile.status === 'sent_to_bank' ? 'success' : 'default'}
            size="small"
          />
        </ListItemSecondaryAction>
      </ListItem>
    )), [aboFiles]
  );

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">
            Poslední platební soubory
          </Typography>
        </Box>
        <List>
          {aboItems}
        </List>
      </CardContent>
    </Card>
  );
});

OptimizedRecentABOFiles.displayName = 'OptimizedRecentABOFiles';
